import React from "react";
import Modal from "../../components/Modal";
import Button from "../../components/Button";
import AttributeHistorySection from "../../components/AttributeHistorySection";

/**
 * Time Machine #114 Phase 6 — minimal modal showing every attribute-change
 * event for one (payrollNo, date) cell. Opened by clicking an attribute
 * pseudo-job in the time-travel schedule grid.
 *
 * Closes the helpdesk loop:
 *   user: "I set an attribute on installer X for day Y, but now it's gone."
 *   support: open Time Machine → find the cell → click → see the full
 *            lifecycle of that cell (every create/update/delete with
 *            timestamp + user attribution).
 *
 * Props:
 *   payrollNo              — formatted payrollNo string
 *   date                   — MM/DD/YYYY date string
 *   setOpenAttributeHistoryModal — setter that closes via {show:false}
 *
 * Layout: same fixed-inner-height pattern as TimeMachineCrewModal so the
 * modal sits at max-h-[90%] from the start. Without this, the History
 * row's text moves under the user's mouse cursor between the two clicks of
 * a double-click and the dblclick-to-scrub fails to register (see
 * 0c6900d for the original CrewModal fix).
 */
const TimeMachineAttributeHistoryModal = ({
  payrollNo,
  date,
  setOpenAttributeHistoryModal,
}) => {
  const close = () => setOpenAttributeHistoryModal({ show: false });

  return (
    <Modal onClose={close}>
      <Modal.Header>
        Attribute history: {payrollNo || "(no payrollNo)"} · {date || "(no date)"}
      </Modal.Header>
      <Modal.Content>
        <div
          className="w-[700px] flex flex-col gap-3"
          style={{ height: "75vh" }}
        >
          <div className="grid grid-cols-[100px_auto] gap-y-1 shrink-0 text-sm">
            <div>Payroll#</div>
            <div className="font-bold">{payrollNo || "(none)"}</div>
            <div>Date</div>
            <div className="font-bold">{date || "(none)"}</div>
            <div className="col-span-2 italic opacity-75 mt-1">
              Every create / update / delete event for this attribute cell.
              Single-click a row to expand its before/after detail. Double-
              click to scrub the time slider to that exact moment.
            </div>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto">
            <AttributeHistorySection
              payrollNo={payrollNo}
              date={date}
              onScrubbed={close}
            />
          </div>
        </div>
      </Modal.Content>
      <Modal.Footer>
        <div className="h-8 flex">
          <Button color="light" className="uppercase w-31" onClick={close}>
            Close
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default TimeMachineAttributeHistoryModal;
